import { useContext, useEffect } from 'react';
import {
  DisplayTimeZones,
  DisplayTourTypes,
  EditListingTourConfigSteps,
  TOUR_DURATION_DISPLAY_MAP,
  type TimeZoneIds,
} from '@zg-rentals/touring';
import { Box, Text, TextButton } from '@zillow/constellation-10';

import TouringAvailability from './TouringAvailability';
import AccordionIconError from '../AccordionIconError';
import ContentSectionEditButton from '../ContentSectionEditButton';
import FieldValueDisplay from '../FieldValueDisplay';
import RecommendationIconBulb from '../../../../RecommendationIconBulb';
import { useTourSchedulingContext } from '../../../../WizardModularListing/TourSchedulingSetup/context';
import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../../constants/RecommendationsConstants';
import { RMWContext } from '../../../../../providers/RMWContext';
import { useSelector } from '../../../../../selectors/useSelector';
import { getFullCalendarPageUrl } from '../../../../../utils/touringUtils';
import TouringModularListingCSEvents from '../../../../../model/ClickstreamEvent/Touring/TouringModularListingCSEvents';
import accessUtils from '../../../../../utils/accessUtils';

import {
  StyledIconPlaceholder,
  StyledReviewSection,
  StyledReviewSectionTitle,
  StyledReviewTitleRow,
} from '../../styles';

const { CONTACT } = REVIEW_PAGE_SECTION_RECS;

const TOUR_BOOKING_METHOD = 'tour-booking-method';

type TouringSectionProps = {
  onClickEditTourSection: (modalStep: EditListingTourConfigSteps) => void;
  onClickRecommendation: (recommendationType: string) => void;
};

const TouringSection = ({ onClickEditTourSection, onClickRecommendation }: TouringSectionProps) => {
  const { paths } = useContext(RMWContext);
  const user = useSelector((state) => state.user);
  const appConfig = useSelector((state) => state.app.appConfig);
  const canAccessPostingPathTourDuration = accessUtils.canAccessPostingPathTourDuration({ appConfig });
  const {
    recommendations,
    currentProperty,
    currentProperty: { active, listingAlias },
  } = useSelector((state) => state.properties);
  const tourCalendarPath = getFullCalendarPageUrl(listingAlias, paths);

  const { availabilityDates, selectedBookingMethod, selectedTimeZone, selectedTourDuration, tourCalendarApiHook } =
    useTourSchedulingContext();
  const isTouringSchedulingEnabled = Boolean(selectedBookingMethod && selectedTimeZone);

  const handleEditBookingMethodClick = () => {
    if (selectedBookingMethod) {
      return onClickEditTourSection(EditListingTourConfigSteps.EDIT_BOOKING_METHOD);
    }

    return onClickEditTourSection(EditListingTourConfigSteps.SET_BOOKING_METHOD);
  };

  const handleEditAvailabilityClick = () => {
    return onClickEditTourSection(EditListingTourConfigSteps.EDIT_AVAILABILITY);
  };

  const handleEditTourDurationClick = () => {
    return onClickEditTourSection(EditListingTourConfigSteps.EDIT_TOUR_DURATION);
  };

  useEffect(() => {
    if (tourCalendarApiHook.isTourConfigLoading) {
      return;
    }

    TouringModularListingCSEvents.ReviewStepBookingMethodInitialStateImpression({
      currentProperty,
      user,
      isActive: active,
      tourConfirmationType: tourCalendarApiHook.tourConfirmationType,
    });
  }, [tourCalendarApiHook.isTourConfigLoading]);

  return (
    <>
      <StyledReviewSection data-testid="TouringSection-TourType">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {recommendations[CONTACT][RECS.SCHEDULE] && (
                <RecommendationIconBulb
                  fieldName={TOUR_BOOKING_METHOD}
                  removeFlex={true}
                  onClick={() => {
                    onClickRecommendation(RECS.SCHEDULE);
                  }}
                />
              )}
            </StyledIconPlaceholder>

            <StyledReviewSectionTitle>
              <Text css={{ textStyle: 'bodyHeading' }}>Tour booking method</Text>

              {!isTouringSchedulingEnabled && !active && (
                <TextButton
                  aria-label={`learn-more-about-${TOUR_BOOKING_METHOD}`}
                  data-testid="TouringSection-LearnMore"
                  textStyle="body"
                  onClick={() => {
                    onClickRecommendation(RECS.SCHEDULE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
              {active && (
                <TextButton
                  asChild
                  aria-label={`learn-more-about-${TOUR_BOOKING_METHOD}`}
                  data-testid="TouringSection-CalendarLink"
                  textStyle="body"
                >
                  <a href={tourCalendarPath}>View calendar to edit</a>
                </TextButton>
              )}
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            onEditClick={handleEditBookingMethodClick}
            useLockIcon={active}
            disabledMessage="To change your booking method while your listing is live, go to your booking calendar."
            dataEventId={TouringModularListingCSEvents.ReviewStepEditBookingMethodClick.data_event_id}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={selectedBookingMethod ? DisplayTourTypes[selectedBookingMethod] : 'Not set'} />
      </StyledReviewSection>

      {isTouringSchedulingEnabled && (
        <>
          {canAccessPostingPathTourDuration && (
            <StyledReviewSection
              data-testid="TouringSection-TourDuration"
              data-eventid={TouringModularListingCSEvents.ReviewStepTourDurationImpression.data_event_id}
            >
              <StyledReviewTitleRow>
                <Box css={{ display: 'flex', alignItems: 'center' }}>
                  <StyledIconPlaceholder />
                  <Text textStyle="body-bold">Length of each tour</Text>
                </Box>
                <ContentSectionEditButton
                  onEditClick={handleEditTourDurationClick}
                  useLockIcon={active}
                  disabledMessage="To change tour length while your listing is live, go to your booking calendar."
                  dataEventId={TouringModularListingCSEvents.ReviewStepTourDurationClick.data_event_id}
                />
              </StyledReviewTitleRow>
              <FieldValueDisplay
                value={selectedTourDuration ? TOUR_DURATION_DISPLAY_MAP[selectedTourDuration] : 'Not set'}
              />
            </StyledReviewSection>
          )}

          <StyledReviewSection data-testid="TouringSection-TimeZone">
            <StyledReviewTitleRow>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <StyledIconPlaceholder />
                <Text css={{ textStyle: 'bodyHeading' }}>Time zone of the property</Text>
              </Box>
            </StyledReviewTitleRow>
            <FieldValueDisplay value={DisplayTimeZones[selectedTimeZone as TimeZoneIds]} />
          </StyledReviewSection>

          <StyledReviewSection
            data-testid="TouringSection-Availability"
            data-eventid={TouringModularListingCSEvents.ReviewStepEditAvailabilityImpression.data_event_id}
          >
            <StyledReviewTitleRow>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <StyledIconPlaceholder>
                  {isTouringSchedulingEnabled && !availabilityDates.length && (
                    <AccordionIconError tabIndex={0} title="Tour availability incomplete error" marginRight="xs" />
                  )}
                </StyledIconPlaceholder>
                <Text css={{ textStyle: 'bodyHeading' }}>Tour availability</Text>
              </Box>

              <ContentSectionEditButton
                onEditClick={handleEditAvailabilityClick}
                useLockIcon={active}
                disabledMessage="To add tour availability while your listing is live, go to your booking calendar."
                dataEventId={TouringModularListingCSEvents.ReviewStepEditAvailabilityClick.data_event_id}
              />
            </StyledReviewTitleRow>
            <FieldValueDisplay customDisplayComponent={<TouringAvailability availabilityDates={availabilityDates} />} />
          </StyledReviewSection>
        </>
      )}
    </>
  );
};

export default TouringSection;
